import { ReactComponent as DownloadGlyph } from '@/assets/images/svg/glyphs/download.svg';
import { ReactComponent as EmailGlyph } from '@/assets/images/svg/glyphs/email.svg';
import { ReactComponent as LinkGlyph } from '@/assets/images/svg/glyphs/link.svg';
import { SOCIAL_MEDIA_ACCOUNTS } from '@/constants/social';
import { GlobalContext } from '@/context';
import gtm from '@/lib/gtm';
import { openSocialShareWindow } from '@/lib/social';
import { copyToClipboard } from '@/lib/utils';
import { COLORS } from '@/styles/color';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { remCalc } from '@/styles/typography/utils';
import { capitalize } from 'lodash';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const ANALYTICS_LOCATION = 'Share Buttons';

const SHARE_ICONS = SOCIAL_MEDIA_ACCOUNTS.filter((account) =>
  ['twitter', 'facebook', 'linkedin'].includes(account.handle),
);

const ShareButtonWrapper = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
  flex-wrap: wrap;
  column-gap: var(--share-button-gap, ${remCalc(10)});
  row-gap: var(--share-button-gap, ${remCalc(10)});
`;

const ShareButton = styled.button`
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(8)};
  position: relative;

  svg {
    width: var(--share-button-size, ${remCalc(40)});

    path {
      fill: var(--share-button-color, ${COLORS.BLACK});
    }
  }

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(3)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding: ${remCalc(6)};
  }
`;

const Popup = styled.div`
  position: absolute;
  font-size: ${remCalc(14)};
  top: -${remCalc(34)};
  left: -${remCalc(24)};
  color: ${COLORS.GREEN.BASE};
  background-color: ${COLORS.GREEN.TINT};
  border: 1px solid ${COLORS.GREEN.BASE};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(4)} ${remCalc(8)};
  white-space: nowrap;
`;

interface ShareButtonsProps extends PropsWithClassName {
  url: string;
  text: string;
  downloadUrl?: string;
  email?: string;
}

const ShareButtons: FC<ShareButtonsProps> = (props) => {
  const { metadata } = useContext(GlobalContext);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (showPopup) {
      setTimeout(() => setShowPopup(false), 2000);
    }
  }, [showPopup]);

  const socialMediaButtons = useMemo(() => {
    return SHARE_ICONS.map(({ handle, icon, shareUrl }) => {
      const socialAccount = metadata?.sameAsLinks[handle];

      if (!socialAccount || !shareUrl) return null;

      return (
        <ShareButton
          key={socialAccount.handle}
          title={`Share this to ${capitalize(socialAccount.handle)}`}
          onClick={() => {
            openSocialShareWindow(shareUrl(props.url, props.text));

            gtm.track('click', {
              element_type: 'button',
              element_label: capitalize(socialAccount.handle),
              location: ANALYTICS_LOCATION,
            });
          }}
        >
          {icon}
        </ShareButton>
      );
    });
  }, []);

  return (
    <ShareButtonWrapper className={props.className}>
      {socialMediaButtons}

      {props.email && (
        <ShareButton
          as="a"
          href={`mailto:?body=${encodeURIComponent(props.email)}`}
          title="Email to a friend"
          onClick={() => {
            gtm.track('click', {
              element_type: 'button',
              element_label: 'Email',
              location: ANALYTICS_LOCATION,
            });
          }}
        >
          <EmailGlyph />
        </ShareButton>
      )}

      <ShareButton
        title="Copy a link to your clipboard"
        onClick={() => {
          copyToClipboard(props.url);
          setShowPopup(true);

          gtm.track('click', {
            element_type: 'button',
            element_label: 'Copy to clipboard',
            location: ANALYTICS_LOCATION,
          });
        }}
      >
        <LinkGlyph />
        {showPopup && <Popup>Link copied!</Popup>}
      </ShareButton>

      {props.downloadUrl && (
        <ShareButton
          as="a"
          href={props.downloadUrl}
          title="Download"
          target="_blank"
          download
          onClick={() => {
            gtm.track('click', {
              element_type: 'button',
              element_label: 'Download',
              location: ANALYTICS_LOCATION,
            });
          }}
        >
          <DownloadGlyph />
        </ShareButton>
      )}
    </ShareButtonWrapper>
  );
};

export default ShareButtons;
